import React from 'react';
import {
  Box,
  Avatar,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  Flex,
  Icon,
  Tooltip,
  Divider,
} from '@chakra-ui/react';
import { getRoleDisplay } from "utils/roleLabel";
import { useUserAuth } from "utils/site";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import { 
  FiMoreVertical, 
  FiUser, 
  FiMail, 
  FiPhone, 
  FiCalendar, 
  FiEdit2, 
  FiEye, 
  FiUserPlus,
  FiCreditCard,
  FiFileText,
  FiUsers
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

// Componente de Card para Usuarios/Estudiantes
export const StudentCard = ({ 
  student, 
  onEdit, 
  onViewProfile, 
  onAddPayment, 
  showActions = true,
  compact = false 
}) => {
  const { user: authUser } = useUserAuth();
  const isAdmin = authUser?.role === 'administrador';
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('primary.50', 'primary.900');

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'secondary';
      case 'inactive': return 'gray';
      case 'pending': return 'accent';
      default: return 'gray';
    }
  };

  const getGroupBadge = (group) => {
    if (!group || group === 'empty') return null;
    return (
      <Badge 
        colorScheme="primary" 
        variant="subtle" 
        fontSize="xs"
        borderRadius="full"
        px={2}
        maxW="120px"
        isTruncated
        title={group}
      >
        {group}
      </Badge>
    );
  };

  return (
    <Card
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="16px"
      overflow="hidden"
      transition="all 0.2s ease-in-out"
      _hover={{
        borderColor: 'primary.300',
        boxShadow: '0 8px 32px rgba(24, 144, 255, 0.1)',
        transform: 'translateY(-2px)',
      }}
      cursor={onViewProfile ? 'pointer' : 'default'}
      onClick={onViewProfile}
    >
      <CardHeader pb={compact ? 2 : 4}>
        <Flex justify="space-between" align="flex-start">
          <HStack spacing={3}>
            <Avatar
              size={compact ? 'sm' : 'md'}
              name={`${student.first_name} ${student.last_name}`}
              src={student.avatar_url}
              bg="primary.100"
              color="primary.600"
            />
            <VStack align="flex-start" spacing={1}>
              <Text 
                fontWeight="semibold" 
                fontSize={compact ? 'sm' : 'md'}
                color="gray.800"
              >
                {student.first_name} {student.last_name}
              </Text>
              <HStack spacing={2}>
                <Icon as={FiUser} color={textSecondary} size="14px" />
                <Text fontSize="sm" color={textSecondary}>
                  {student.username}
                </Text>
              </HStack>
            </VStack>
          </HStack>

          <VStack align="flex-end" spacing={1}>
            {(() => {
              const { key, label } = getRoleDisplay(student.displayRole ?? student.role, isAdmin);
              const scheme = key === 'customer' ? getStatusColor('active')
                : key === 'becado' ? 'purple'
                : key === 'cobranza' ? 'orange'
                : getStatusColor('inactive');
              return (
                <Badge colorScheme={scheme} borderRadius="full" fontSize="xs">
                  {label}
                </Badge>
              );
            })()}
            {!compact && showActions && (
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<FiMoreVertical />}
                  variant="ghost"
                  size="sm"
                  onClick={(e) => e.stopPropagation()}
                />
                <MenuList>
                  <MenuItem icon={<FiEye />} onClick={(e) => { e.stopPropagation(); onViewProfile?.(); }}>
                    Ver Perfil
                  </MenuItem>
                  <MenuItem icon={<FiEdit2 />} onClick={(e) => { e.stopPropagation(); onEdit?.(); }}>
                    Editar
                  </MenuItem>
                  <MenuItem icon={<FiCreditCard />} onClick={(e) => { e.stopPropagation(); onAddPayment?.(); }}>
                    Agregar Pago
                  </MenuItem>
                </MenuList>
              </Menu>
            )}
          </VStack>
        </Flex>
      </CardHeader>

      {!compact && (
        <CardBody pt={0}>
          <VStack spacing={3} align="stretch">
            <HStack spacing={4}>
              <HStack spacing={2} flex={1}>
                <Icon as={FiMail} color={textSecondary} size="14px" />
                <Text fontSize="sm" color={textSecondary} noOfLines={1}>
                  {student.email}
                </Text>
              </HStack>
              {student.phone && (
                <HStack spacing={2}>
                  <Icon as={FiPhone} color={textSecondary} size="14px" />
                  <Text fontSize="sm" color={textSecondary}>
                    {student.phone}
                  </Text>
                </HStack>
              )}
            </HStack>

            <HStack justify="space-between">
              <HStack spacing={2}>
                <Icon as={FiUsers} color={textSecondary} size="14px" />
                {getGroupBadge(student.meta_data?.find(m => m.key === 'group_name')?.value) || (
                  <Text fontSize="sm" color={textSecondary}>Sin grupo</Text>
                )}
              </HStack>
              
              {student.date_created && (
                <HStack spacing={2}>
                  <Icon as={FiCalendar} color={textSecondary} size="14px" />
                  <Text fontSize="sm" color={textSecondary}>
                    {new Date(student.date_created).toLocaleDateString()}
                  </Text>
                </HStack>
              )}
            </HStack>

            {showActions && (
              <>
                <Divider />
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="educational-ghost"
                    leftIcon={<FiEye />}
                    onClick={(e) => { e.stopPropagation(); onViewProfile?.(); }}
                    flex={1}
                  >
                    Ver Perfil
                  </Button>
                  <Button
                    size="sm"
                    variant="educational-outline"
                    leftIcon={<FiCreditCard />}
                    onClick={(e) => { e.stopPropagation(); onAddPayment?.(); }}
                    flex={1}
                  >
                    Pago
                  </Button>
                </HStack>
              </>
            )}
          </VStack>
        </CardBody>
      )}
    </Card>
  );
};

// Componente de Card para Métricas Educativas
export const MetricCard = ({ 
  title, 
  value, 
  icon, 
  trend, 
  trendValue, 
  color = 'primary',
  onClick,
  compact = false
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');

  const trendColor = trend === 'up' ? 'secondary.500' : 
                    trend === 'down' ? 'accent.500' : textSecondary;

  return (
    <Card
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius={compact ? "12px" : "16px"}
      overflow="hidden"
      transition="all 0.2s ease-in-out"
      _hover={onClick ? {
        borderColor: `${color}.300`,
        boxShadow: `0 8px 32px rgba(24, 144, 255, 0.1)`,
        transform: 'translateY(-2px)',
      } : {}}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      size={compact ? "sm" : "md"}
    >
      <CardBody p={compact ? 4 : 6}>
        {compact ? (
          <VStack spacing={2} align="center">
            <Box
              p={2}
              borderRadius="8px"
              bg={`${color}.50`}
              color={`${color}.600`}
            >
              <Icon as={icon} boxSize={4} />
            </Box>
            <Text fontSize="xs" color={textSecondary} fontWeight="medium" textAlign="center">
              {title}
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="gray.800">
              {value}
            </Text>
          </VStack>
        ) : (
          <HStack justify="space-between" align="flex-start">
            <VStack align="flex-start" spacing={2} flex={1}>
              <Text fontSize="sm" color={textSecondary} fontWeight="medium">
                {title}
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color="gray.800">
                {value}
              </Text>
              {trendValue && (
                <HStack spacing={1}>
                  <Text fontSize="sm" color={trendColor} fontWeight="medium">
                    {trend === 'up' ? '↗' : trend === 'down' ? '↘' : '→'} {trendValue}
                  </Text>
                  <Text fontSize="sm" color={textSecondary}>
                    vs mes anterior
                  </Text>
                </HStack>
              )}
            </VStack>
            
            <Box
              p={3}
              borderRadius="12px"
              bg={`${color}.50`}
              color={`${color}.600`}
            >
              <Icon as={icon} boxSize={6} />
            </Box>
          </HStack>
        )}
      </CardBody>
    </Card>
  );
};

// Componente de Card para Información General
export const InfoCard = ({ 
  title, 
  children, 
  icon, 
  action,
  variant = 'default' 
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = variant === 'highlighted' ? 
    useColorModeValue('primary.50', 'primary.900') : 
    'transparent';

  return (
    <Card
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="16px"
      overflow="hidden"
    >
      <CardHeader bg={headerBg}>
        <HStack justify="space-between" align="center">
          <HStack spacing={3}>
            {icon && (
              <Box
                p={2}
                borderRadius="8px"
                bg="primary.100"
                color="primary.600"
              >
                <Icon as={icon} boxSize={4} />
              </Box>
            )}
            <Text fontWeight="semibold" fontSize="lg">
              {title}
            </Text>
          </HStack>
          {action}
        </HStack>
      </CardHeader>
      <CardBody>
        {children}
      </CardBody>
    </Card>
  );
};